import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useRef,
} from "react";
import { useCustom } from "../hooks/useCustomization";
import { defaultCustomization } from "./CustomizationContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useModules } from "./ModulesContext";
import { setSessionFlag, clearSessionFlag } from "../utils/faceIdGate";
import { clearNativeRefreshToken } from "../utils/nativeTokenStore";
import {
  noteSessionResponse,
  clearSessionExpiry,
  SESSION_ENDED_EVENT,
} from "../utils/loginUtils";
import { useSessionRefreshOnActivity } from "../hooks/useSessionRefreshOnActivity";
import { isMobileApp } from "../utils/isMobileApp";
import { flushBackendPush as flushCaddieGamesPush } from "../pages/CaddieGames/caddieGamesStore";
import { getAppContext } from "../utils/getAppContext";
import { getBrandConfig } from "../utils/brandConfig";

export type Role = string;

type RoleSet = {
  orgRoles: Role[];
  platformRoles: Role[];
  systemRoles: Role[];
  permissions: string[];
  defaultModule?: string;
};

type OrgInfo = {
  orgID: string;
  isAdminOrg?: boolean;
  // Configurable header banner for admin orgs (CGAdmin, …). Set from /session.
  bannerName?: string;
  bannerColor?: string;
  // Sub-admin org's purpose key + label — picks which admin console it gets.
  adminPurpose?: string;
  adminLabel?: string;
  // True only for the MapleCoin_Operations org, decided by the backend from
  // its configured org ID (never the name). The server enforces it again.
  mapleCoinOperations?: boolean;
};

// Hard-refresh on a protected route blanks AuthContext's `org` to "" until
// /session resolves. During that window, components that key off org.orgID
// (entitlements fetch, Header, customization fetch) all see an empty id.
// Caching the last-known orgID in localStorage gives initial render a
// head start — /session always overwrites if it disagrees, so the cache
// can't go stale beyond the next session round-trip.
const ORG_CACHE_KEY = "asksmart.cachedOrgID";

// Mobile detection — same formula App.js and Header use for mobileMode.
// Exposed on the context so pages can adapt their layout without
// re-deriving the Capacitor check + UA sniff themselves.
const IS_MOBILE =
  isMobileApp() ||
  window.navigator.userAgent.includes("iPhone") ||
  window.navigator.userAgent.includes("iPad");

function readCachedOrgID(): string {
  try {
    return localStorage.getItem(ORG_CACHE_KEY) || "";
  } catch {
    return "";
  }
}

interface ToolEntitlement {
  enabled: boolean;
  [key: string]: any;
}

export interface EffectiveEntitlements {
  features: Record<string, ToolEntitlement>;
  limits: Record<string, number>;
  defaultModule: string;
}

type UsageLimitFlags = Record<string, boolean>; // true = limit reached/exceeded

interface AuthContextType {
  isAuthenticated: boolean | null;
  isMobile: boolean;
  user: any;
  roles: RoleSet;
  org: OrgInfo;
  canSwitchOrgs: boolean;
  planStatus: PlanStatus;
  isPlatformAdminMode: boolean | null;
  entitlements: EffectiveEntitlements | null;
  postLoginDestination: string | null;
  fetchSession: () => Promise<boolean>;
  refreshEntitlements: () => Promise<void>;
  fetchUsageLimitFlags: (
    forceRefresh?: boolean,
  ) => Promise<UsageLimitFlags | null>;
  updateUsageLimitFlags: (limit: string, flag: boolean) => void;
  usageLimitFlags: UsageLimitFlags;
  logout: () => Promise<void>;
}

type PlanStatus = "active" | "suspended" | null;

const AuthContext = createContext<AuthContextType>({} as AuthContextType);
export const useAuthentication = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { modules: catalogModules, refresh: refreshModules } = useModules();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<any>(null);
  const [roles, setRoles] = useState<RoleSet>({
    orgRoles: [],
    platformRoles: [],
    systemRoles: [],
    permissions: [],
  });
  const [org, setOrg] = useState<OrgInfo>(() => ({
    orgID: readCachedOrgID(),
  }));
  const [canSwitchOrgs, setCanSwitchOrgs] = useState(false);
  const [isPlatformAdminMode, setPlatformAdminMode] = useState<boolean | null>(
    null,
  );
  const [planStatus, setPlanStatus] = useState<PlanStatus>("active");
  const [usageLimitFlags, setUsageLimitFlags] = useState<UsageLimitFlags>({});
  const [postLoginDestination, setPostLoginDestination] = useState<string | null>(null);

  // ── Entitlements — stored in state, fetched once per org, no per-component fetching ──
  const [entitlements, setEntitlements] =
    useState<EffectiveEntitlements | null>(null);

  const usageFlagsCache = useRef<UsageLimitFlags | null>(null);

  // Mirror org changes to localStorage so the next hard refresh starts
  // with a non-empty orgID. Logout already setOrg({ orgID: "" }), which
  // this effect mirrors as a remove → cache cleared on sign-out.
  useEffect(() => {
    try {
      if (org.orgID) {
        localStorage.setItem(ORG_CACHE_KEY, org.orgID);
      } else {
        localStorage.removeItem(ORG_CACHE_KEY);
      }
    } catch {
      // private mode / quota — best effort
    }
  }, [org.orgID]);

  const { setCustomization } = useCustom();

  // TODO: is this missing any paths like queries or content management?
  const protectedPaths = [
    "/chat",
    "/chatbot",
    "/manage-chat",
    "/admin",
    "/superadmin",
    "/switch-org",
    "/unauthorized",
    "/orgchart",
    "/variance",
    "/stock",
    "/call",
    "/waitlist",
    // SMRT modules
    "/smrt-docs",
    "/smrt-shifts",
    "/smrt-bar",
    "/smrt-kitchen",
    "/profiles",
    "/smrt-pos",
    "/smrt-tenant",
    "/smrt-sites",
    "/smrt-time-variance",
    "/smrt-access",
    "/smrt-cop",
    "/smrt-tasks",
    "/smrt-crmtix",
    "/smrt-timecheck",
    // SMRT admin
    "/manage-profiles",
    "/manage-smrt-docs",
    "/manage-smrt-tenant",
    "/manage-smrt-cop",
    "/manage-smrt-tasks",
    "/manage-smrt-timecheck",
    "/login-as",
    "/admin/profile",
  ];

  const scaffoldedPaths = catalogModules
    .filter((m) => m.scaffolded && m.status === "active")
    .flatMap((m) => [m.route, m.adminRoute].filter(Boolean) as string[]);

  const allProtectedPaths = [...protectedPaths, ...scaffoldedPaths];

  const isOnProtectedRoute = allProtectedPaths.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  // ── Entitlements — internal fetch, exposed via state only ────────────────────
  const fetchEntitlements = useCallback(async (): Promise<void> => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/entitlements`,
        {
          method: "GET",
          credentials: "include",
        },
      );
      if (!res.ok) throw new Error("Failed to fetch entitlements");
      const data = await res.json();
      setEntitlements(data);
    } catch (err) {
      console.error("Error fetching org entitlements:", err);
      setEntitlements(null);
    }
  }, []);

  // Exposed for force-refresh scenarios (e.g. after plan upgrade)
  const refreshEntitlements = useCallback(async (): Promise<void> => {
    await fetchEntitlements();
  }, [fetchEntitlements]);

  // ── Can-switch-orgs ───────────────────────────────────────────────────────────
  // Counts orgs via the brand-filtered picker feed (the backend derives AS vs CG
  // from the session's id_token), NOT the raw /management/user/orgs list — a
  // CaddieGames user with one CG org and several AskSmart orgs must not see
  // "Switch Org" in CaddieGames: the picker would only offer the org they're in.
  useEffect(() => {
    if (!user) return;

    const fetchCanSwitch = async (): Promise<void> => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/org-selection/options`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!response.ok) throw new Error(`options ${response.status}`);
        const data = await response.json();
        const orgs = Array.isArray(data.organizations)
          ? data.organizations
          : [];
        setCanSwitchOrgs(orgs.length > 1);
      } catch (err) {
        console.error("Failed to fetch orgs for user switching:", err);
        setCanSwitchOrgs(false);
      }
    };

    fetchCanSwitch();
  }, [user]);

  // ── Plan status ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!org.orgID) return;

    const fetchPlanStatus = async (): Promise<void> => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/org/${org.orgID}/status`,
          { method: "GET", credentials: "include" },
        );
        if (!response.ok) throw new Error("Failed to fetch plan status");
        const data = await response.json();
        const subscriptionStatus = data.subscription?.status || "suspended";
        setPlanStatus(subscriptionStatus);
      } catch (err) {
        console.error("Error fetching subscription status:", err);
        setPlanStatus(null);
      }
    };

    fetchPlanStatus();
  }, [org.orgID]);

  // ── Usage limit flags ─────────────────────────────────────────────────────────
  const fetchUsageLimitFlags = useCallback(
    async (forceRefresh = false): Promise<UsageLimitFlags | null> => {
      if (!org.orgID) return null;

      if (!forceRefresh && usageFlagsCache.current) {
        setUsageLimitFlags(usageFlagsCache.current);
        return usageFlagsCache.current;
      }

      try {
        const res = { ok: true, json: async () => ({}) } as any;
        if (!res.ok) throw new Error("Failed to fetch usage limit flags");
        const data = await res.json();
        usageFlagsCache.current = data;
        setUsageLimitFlags(data);
        return data;
      } catch (err) {
        console.error("Error fetching usage limit flags:", err);
        return null;
      }
    },
    [org.orgID],
  );

  const updateUsageLimitFlags = useCallback((limit: string, flag: boolean) => {
    setUsageLimitFlags((prev) => ({ ...prev, [limit]: flag }));
  }, []);

  // ── Session ───────────────────────────────────────────────────────────────────
  const fetchSession = useCallback(async (): Promise<boolean> => {
    if (isAuthenticated === true) {
      return true;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/session`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Not logged in");
      const data = await res.json();
      noteSessionResponse(data);

      let roles = data.roles;

      // If DB permission hydration failed (empty permissions with populated platformRoles),
      // retry session once to give the DB a second chance
      if (
        roles.permissions?.length === 0 &&
        roles.platformRoles?.length > 0
      ) {
        try {
          const retryRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/session`, {
            credentials: "include",
          });
          if (retryRes.ok) {
            const retryData = await retryRes.json();
            if (retryData.roles.permissions?.length > 0) {
              roles = retryData.roles;
            }
          }
        } catch {
          // Use original data if retry fails
        }
      }

      setUser(data.user);
      setRoles(roles);
      setOrg(data.org);
      setIsAuthenticated(true);
      setPostLoginDestination(data.postLoginDestination ?? null);

      // Customization is now inlined on the /session response — apply it
      // immediately so the header paints with org branding on the very first
      // post-login render. Falls back to defaults if the lookup failed
      // server-side (data.customization will be null).
      if (data.customization) {
        setCustomization(data.customization);
      } else {
        // No org branding (e.g. the platform admin org). Apply the default
        // styling and mark customization ready so the header renders with the
        // default banner instead of staying hidden behind the awaiting gate.
        setCustomization(defaultCustomization);
      }

      await fetchEntitlements();

      // Super-Admin is a system role, not a platform role. Platform-admin mode
      // applies in AskAdmin AND any internal admin org (CGAdmin, …) — the
      // backend flags those via org.isAdminOrg — so they land on an admin
      // console instead of hanging on missing module entitlements/permissions.
      setPlatformAdminMode(
        data.roles.systemRoles.includes("Super-Admin") &&
          (data.org.orgID === process.env.REACT_APP_ASK_ADMIN_ORG_ID ||
            data.org.isAdminOrg === true),
      );

      return true;
    } catch (err) {
      // Diagnostic — distinguishes "session genuinely 401'd" (the user
      // gets bounced through HomeRedirect → /auth) from "session valid
      // but org/entitlements slow to populate" (UX flicker only). The
      // refresh-to-Auth0 bug we're chasing lives in the former bucket;
      // logging keeps it identifiable without instrumenting downstream.
      console.warn("[AuthContext] fetchSession failed:", err);
      setUser(null);
      setRoles({
        orgRoles: [],
        platformRoles: [],
        systemRoles: [],
        permissions: [],
      });
      setOrg({ orgID: "" });
      setPlatformAdminMode(false);
      setIsAuthenticated(false);
      setUsageLimitFlags({});
      return false;
    }
  }, [fetchEntitlements, isAuthenticated]);

  // ── Logout ────────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    // Flush any pending CaddieGames blob push first. The store debounces
    // PUTs to 500ms so a user who taps logout right after a score edit
    // would otherwise lose that last mutation. Best-effort: failures
    // don't block logout. Runs while the auth cookie is still attached.
    await flushCaddieGamesPush().catch(() => undefined);

    // Best-effort: deregister this device's FCM token before clearing the
    // session, so a tapped notification on a shared device doesn't wake the
    // wrong account. Failures (no token stored, network) are swallowed —
    // logout must always proceed.
    try {
      const tokenId = localStorage.getItem("asksmart.deviceTokenId");
      if (tokenId) {
        await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/device-tokens/${encodeURIComponent(tokenId)}`,
          { method: "DELETE", credentials: "include" },
        ).catch(() => undefined);
        localStorage.removeItem("asksmart.deviceTokenId");
      }
    } catch {
      // localStorage unavailable — nothing to do
    }

    setUser(null);
    setRoles({
      orgRoles: [],
      platformRoles: [],
      systemRoles: [],
      permissions: [],
    });
    setOrg({ orgID: "" });
    setEntitlements(null);
    usageFlagsCache.current = null;
    setUsageLimitFlags({});
    setIsAuthenticated(false);
    setPostLoginDestination(null);
    setCustomization();
    clearSessionFlag();
    clearSessionExpiry();
    // Belt and braces: delete any refresh token an older release stashed natively.
    clearNativeRefreshToken().catch(() => undefined);
    // Mark that the upcoming /auth call must force a login form (not honor
    // SSO). HomeRedirect reads this flag and appends ?force=true so the
    // backend adds prompt=login to /authorize. Without this, Auth0 silently
    // re-auths via the SSO cookie that v2/logout sometimes can't fully kill.
    try { sessionStorage.setItem("asksmart.forceReAuth", "1"); } catch (_) {}

    // POST to /logout and await the response before navigating. This guarantees
    // the browser processes the Set-Cookie clear headers before any page load
    // runs — closing the race where a quick URL paste could hit a protected
    // route while cookies are still valid (GET /logout could be cancelled by
    // the very navigation it was supposed to precede).
    // platform tells the backend to put ?platform=mobile on the post-logout
    // returnTo (/auth), so the re-login hosted page strips the REGISTER button
    // on mobile. Sent in the POST body and on the GET fallback URL so both
    // logout paths preserve it.
    const platform = isMobileApp() ? "mobile" : "web";
    // Brand hint for the post-logout return (the cookie is gone after account
    // deletion); legacy caddie/isCaddie hints ride along for rolling deploys.
    const brand = getBrandConfig(getAppContext());
    const isCaddie = brand.brand === "caddiegames";
    const logoutQuery = new URLSearchParams();
    if (platform === "mobile") logoutQuery.set("platform", "mobile");
    if (!brand.isDefault) logoutQuery.set("brand", brand.brand);
    if (isCaddie) logoutQuery.set("caddie", "true");
    const qs = logoutQuery.toString();
    const fallbackLogoutUrl = `${process.env.REACT_APP_BACKEND_URL}/logout${
      qs ? `?${qs}` : ""
    }`;
    let logoutUrl = fallbackLogoutUrl;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/logout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform,
          ...(!brand.isDefault ? { brand: brand.brand } : {}),
          ...(isCaddie ? { isCaddie: true } : {}),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.logoutUrl) logoutUrl = data.logoutUrl;
      }
    } catch {
      // Network failure: fall back to GET /logout which also clears cookies.
      logoutUrl = fallbackLogoutUrl;
    }
    window.location.href = logoutUrl;
  }, [setCustomization]);

  // Mark "session exists on this device" once Auth has succeeded. Read by
  // the Face ID gate on next launch to decide whether to prompt biometrics.
  useEffect(() => {
    if (isAuthenticated) setSessionFlag();
  }, [isAuthenticated]);

  // Refresh while the user is present and the token is near its end; the fetchInterceptor's 401 retry stays as the safety net.
  useSessionRefreshOnActivity(isAuthenticated === true);

  // A refresh that answers session_ended (token dead, cookies gone) ends the session here and returns to the brand's door.
  // Connects callback to session ended event so it can only be triggered when user is authenticated and on a page
  useEffect(() => {
    if (isAuthenticated !== true) return;
    const onSessionEnded = () => {
      setUser(null);
      setRoles({ orgRoles: [], platformRoles: [], systemRoles: [], permissions: [] });
      setOrg({ orgID: "" });
      setEntitlements(null);
      usageFlagsCache.current = null;
      setUsageLimitFlags({});
      setPlatformAdminMode(false);
      setPostLoginDestination(null);
      setIsAuthenticated(false);
      clearSessionFlag();
      clearSessionExpiry();
      navigate(getBrandConfig(getAppContext()).loginPath, { replace: true });
    };
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded);
  }, [isAuthenticated, navigate]);

  // ── Bootstrap: fetch session on protected route load ─────────────────────────
  useEffect(() => {
    if (isOnProtectedRoute && isAuthenticated === null) {
      fetchSession();
    }
  }, [isOnProtectedRoute, isAuthenticated, fetchSession]);

  // ── Clear state when leaving protected routes ─────────────────────────────────
  // Customization is intentionally NOT cleared here: branding is a property of
  // the device's last-known org and outlives any single route visit. Wiping it
  // on every transit through "/" or "/auth-success" guarantees a cache miss
  // (and a default-green flash) on the next login. logout() still clears it.
  useEffect(() => {
    if (!isOnProtectedRoute) {
      setOrg({ orgID: "" });
      setEntitlements(null);
      usageFlagsCache.current = null;
      setUsageLimitFlags({});
    }
  }, [isOnProtectedRoute]);

  // ── Refresh Modules catalog once authenticated ────────────────────────────────
  // ModulesProvider mounts above AuthProvider, so its initial fetch may have been
  // unauthenticated (401 → empty). Re-fetch now that the user is signed in.
  useEffect(() => {
    if (isAuthenticated) refreshModules();
  }, [isAuthenticated, refreshModules]);

  // ── Fetch org-scoped data when org is set on a protected route ────────────────
  // Customization is no longer fetched here — it's inlined on the /session
  // response and applied directly inside fetchSession.
  useEffect(() => {
    if (org.orgID && isOnProtectedRoute) {
      fetchEntitlements();
      fetchUsageLimitFlags(true);
    }
  }, [
    org.orgID,
    isOnProtectedRoute,
    fetchEntitlements,
    fetchUsageLimitFlags,
  ]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isMobile: IS_MOBILE,
        user,
        roles,
        org,
        canSwitchOrgs,
        planStatus,
        isPlatformAdminMode,
        entitlements,
        postLoginDestination,
        fetchSession,
        refreshEntitlements,
        fetchUsageLimitFlags,
        updateUsageLimitFlags,
        usageLimitFlags,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
